import { createSlice } from '@reduxjs/toolkit';
import { difference, union } from 'es-toolkit';

import {
    deleteSimilarItemAlertFromList,
    fetchSimilarItemAlert,
    fetchSimilarItemAlerts,
    removeSimilarItemAlert,
    submitSimilarItemAlert,
    toggleAllSimilarItemAlertsEmail,
    toggleSimilarItemAlertEmail,
} from './similarItemAlerts.actions';
import { defaultSimilarItemAlertsSlice, NO_SIMILAR_ITEM_ALERT } from './similarItemAlerts.types';

const similarItemAlertsSlice = createSlice({
    extraReducers: (builder) => {
        builder.addCase(fetchSimilarItemAlert.pending, (slice, { meta }) => {
            slice.loading = union(slice.loading, [meta.arg.lotId]);
        });

        builder.addCase(fetchSimilarItemAlert.fulfilled, (slice, { meta, payload }) => {
            // A lookup that started before a set/remove answers the question as it was, so it must
            // not land on top of the newer write.
            if (!slice.saving.includes(meta.arg.lotId)) {
                slice.byId[meta.arg.lotId] = payload;
                slice.loaded[meta.arg.lotId] = Date.now();
            }
            slice.loading = difference(slice.loading, [meta.arg.lotId]);
        });

        builder.addCase(fetchSimilarItemAlert.rejected, (slice, { meta }) => {
            slice.loading = difference(slice.loading, [meta.arg.lotId]);
        });

        builder.addCase(submitSimilarItemAlert.pending, (slice, { meta }) => {
            slice.saving = union(slice.saving, [meta.arg.lotId]);
        });

        builder.addCase(submitSimilarItemAlert.fulfilled, (slice, { meta, payload }) => {
            slice.byId[meta.arg.lotId] = payload;
            slice.loaded[meta.arg.lotId] = Date.now();
            slice.saving = difference(slice.saving, [meta.arg.lotId]);
        });

        builder.addCase(submitSimilarItemAlert.rejected, (slice, { meta }) => {
            slice.saving = difference(slice.saving, [meta.arg.lotId]);
        });

        builder.addCase(removeSimilarItemAlert.pending, (slice, { meta }) => {
            slice.saving = union(slice.saving, [meta.arg.lotId]);
        });

        builder.addCase(removeSimilarItemAlert.fulfilled, (slice, { meta }) => {
            slice.byId[meta.arg.lotId] = NO_SIMILAR_ITEM_ALERT;
            slice.loaded[meta.arg.lotId] = Date.now();
            slice.saving = difference(slice.saving, [meta.arg.lotId]);
        });

        builder.addCase(removeSimilarItemAlert.rejected, (slice, { meta }) => {
            slice.saving = difference(slice.saving, [meta.arg.lotId]);
        });

        builder.addCase(fetchSimilarItemAlerts.pending, (slice) => {
            slice.list.status = 'loading';
        });

        builder.addCase(fetchSimilarItemAlerts.fulfilled, (slice, { meta, payload }) => {
            // Only a no-keyword fetch sees the whole collection, so it's the only one that can
            // refresh the true total; a filtered fetch keeps the last known unfiltered total.
            const isFiltered = Boolean(meta.arg.keyword?.trim());
            slice.list = {
                items: payload.items,
                page: payload.page,
                pageSize: payload.pageSize,
                status: 'success',
                totalRecords: payload.totalRecords,
                unfilteredTotalRecords: isFiltered ? slice.list.unfilteredTotalRecords : payload.totalRecords,
            };
        });

        builder.addCase(fetchSimilarItemAlerts.rejected, (slice) => {
            slice.list.status = 'error';
        });

        builder.addCase(toggleSimilarItemAlertEmail.fulfilled, (slice, { meta }) => {
            const row = slice.list.items.find((item) => item.savedItemId === meta.arg.savedItemId);
            if (row) {
                row.sendAlert = meta.arg.sendAlert;
            }
        });

        builder.addCase(toggleAllSimilarItemAlertsEmail.fulfilled, (slice, { meta }) => {
            slice.list.items.forEach((item) => {
                item.sendAlert = meta.arg.sendAlert;
            });
        });

        builder.addCase(deleteSimilarItemAlertFromList.fulfilled, (slice, { meta }) => {
            slice.list.items = slice.list.items.filter((item) => item.savedItemId !== meta.arg.savedItemId);
            slice.list.totalRecords = Math.max(0, slice.list.totalRecords - 1);
            // Deleting a row lowers the real total too, so the placeholder count and empty-state
            // check stay honest even while a keyword is active.
            slice.list.unfilteredTotalRecords = Math.max(0, slice.list.unfilteredTotalRecords - 1);
            // Keep the per-lot lookup cache honest so a PDP/Price-Results view of this lot flips to OFF.
            slice.byId[meta.arg.lotId] = NO_SIMILAR_ITEM_ALERT;
            slice.loaded[meta.arg.lotId] = Date.now();
        });
    },
    initialState: defaultSimilarItemAlertsSlice,
    name: 'similarItemAlerts',
    reducers: {},
});

export const { reducer: similarItemAlertsReducer } = similarItemAlertsSlice;
